import { FormEvent, useCallback, useMemo, useState } from "react";
import {
  Stack,
  TextInput,
  Button,
  Group,
  Select,
  Alert,
  Divider,
  Text,
  Modal,
  Loader,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import {
  PortalSubjectType,
  PortalNotificationPreference,
  CreateGrantInput,
  DEFAULT_PORTAL_NOTIFICATION_PREFERENCE,
} from "../../../services/admin/portalAccess.service";
import {
  useCreatePortalAccessGrant,
  usePortalAccessGrantsForSubject,
} from "../../../hooks/admin/usePortalAccess";
import { usePracticePhysicians } from "../../../hooks/admin/usePractices";
import { useSurgeryCenterPhysicians } from "../../../hooks/admin/useSurgeryCentersLabs";
import { usePhysiciansAdmin } from "../../../hooks/admin/usePhysicians";
import {
  compose,
  required,
  email,
  scrollToFirstError,
} from "../../../utils/formValidation";
import {
  classifyPortalGrantError,
  CREATE_GRANT_ERROR_FALLBACKS,
} from "../../../utils/portalAccessErrors";
import { RevokedGrantCard } from "./RevokedGrantCard";
import { PortalNotificationPreferenceSelect } from "./PortalNotificationPreferenceSelect";

export interface PrimaryDefaults {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

export type AddGrantMode = "primary" | "additional";

interface AddPortalGrantFormValues {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  linkedPhysicianId: string;
  notificationPreference: PortalNotificationPreference;
}

interface AddPortalGrantFormProps {
  subjectType: PortalSubjectType;
  subjectId: string;
  mode: AddGrantMode;
  primaryDefaults?: PrimaryDefaults;
  onClose: () => void;
}

interface PhysicianOption {
  value: string;
  label: string;
  email: string | null;
  firstName: string;
  lastName: string;
}

const EMPTY_VALUES: AddPortalGrantFormValues = {
  email: "",
  firstName: "",
  lastName: "",
  role: "",
  linkedPhysicianId: "",
  notificationPreference: DEFAULT_PORTAL_NOTIFICATION_PREFERENCE,
};

export function AddPortalGrantForm({
  subjectType,
  subjectId,
  mode,
  primaryDefaults,
  onClose,
}: AddPortalGrantFormProps) {
  const { data: subjectGrants, isLoading: grantsLoading } =
    usePortalAccessGrantsForSubject(subjectType, subjectId);

  const revokedGrants = useMemo(
    () => (subjectGrants ?? []).filter((g) => g.state === "revoked"),
    [subjectGrants]
  );

  const [addFormExpanded, setAddFormExpanded] = useState(false);

  const handleShowAddForm = useCallback(() => {
    setAddFormExpanded(true);
  }, []);

  // Same reasoning as SubjectGrantForm: lead with any revoked contacts for
  // this subject (restoring is almost always the right action — OBS-108)
  // and only show the invite form once the user opts in.
  const showAddForm = useMemo(
    () => revokedGrants.length === 0 || addFormExpanded,
    [revokedGrants, addFormExpanded]
  );

  const initialValues = useMemo<AddPortalGrantFormValues>(() => {
    if (mode === "primary" && primaryDefaults) {
      return {
        email: primaryDefaults.email,
        firstName: primaryDefaults.firstName,
        lastName: primaryDefaults.lastName,
        role: primaryDefaults.role,
        linkedPhysicianId: "",
        notificationPreference: DEFAULT_PORTAL_NOTIFICATION_PREFERENCE,
      };
    }
    return EMPTY_VALUES;
  }, [mode, primaryDefaults]);

  const form = useForm<AddPortalGrantFormValues>({
    initialValues,
    validate: {
      email: compose(required("Email is required"), email()),
      firstName: required("First name is required"),
      lastName: required("Last name is required"),
    },
    validateInputOnBlur: true,
  });

  const createGrant = useCreatePortalAccessGrant();

  const supportsPhysicianLink = useMemo(
    () => subjectType === "practice" || subjectType === "surgery_center",
    [subjectType]
  );

  const { data: practicePhysicians } = usePracticePhysicians(
    subjectType === "practice" ? subjectId : null
  );
  const { data: surgeryCenterPhysicians } = useSurgeryCenterPhysicians(
    subjectType === "surgery_center" ? subjectId : null
  );
  const { data: allPhysicians } = usePhysiciansAdmin();

  const physicianOptions = useMemo<PhysicianOption[]>(() => {
    if (!supportsPhysicianLink) {
      return [];
    }
    const allById = new Map(
      (allPhysicians ?? []).map((physician) => [physician.id, physician])
    );
    const linkedIds: string[] = [];
    if (subjectType === "practice") {
      (practicePhysicians ?? []).forEach((entry) => {
        linkedIds.push(entry.physicianId);
      });
    } else {
      (surgeryCenterPhysicians ?? []).forEach((entry) => {
        linkedIds.push(entry.physicianId);
      });
    }
    return linkedIds
      .map((id) => allById.get(id))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .map((p) => ({
        value: p.id,
        label: `${p.lastName}, ${p.firstName}`,
        email: p.email ?? null,
        firstName: p.firstName,
        lastName: p.lastName,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    supportsPhysicianLink,
    subjectType,
    practicePhysicians,
    surgeryCenterPhysicians,
    allPhysicians,
  ]);

  const handlePhysicianSelect = useCallback(
    (value: string | null) => {
      form.setFieldValue("linkedPhysicianId", value ?? "");
      if (!value) {
        // Clearing the link releases the role so it can be edited again.
        form.setFieldValue("role", "");
        return;
      }
      const physician = physicianOptions.find((p) => p.value === value);
      if (!physician) {
        return;
      }
      form.setFieldValue("firstName", physician.firstName);
      form.setFieldValue("lastName", physician.lastName);
      if (physician.email) {
        form.setFieldValue("email", physician.email);
      }
      // A grant linked to a physician always carries the Physician role.
      form.setFieldValue("role", "Physician");
    },
    [form, physicianOptions]
  );

  const handleSubmit = useCallback(
    (values: AddPortalGrantFormValues) => {
      const payload: CreateGrantInput = {
        subjectType,
        subjectId,
        email: values.email.trim(),
        firstName: values.firstName.trim() || null,
        lastName: values.lastName.trim() || null,
        role: values.role.trim() || null,
        linkedPhysicianId: values.linkedPhysicianId || null,
        notificationPreference: values.notificationPreference,
      };
      createGrant.mutate(payload, {
        onSuccess: () => {
          notifications.show({
            color: "green",
            message:
              mode === "primary"
                ? "Primary contact invited"
                : "Portal access invitation sent",
          });
          onClose();
        },
        onError: (error) => {
          notifications.show({
            color: "red",
            message: classifyPortalGrantError(
              error,
              CREATE_GRANT_ERROR_FALLBACKS
            ),
          });
        },
      });
    },
    [subjectType, subjectId, createGrant, mode, onClose]
  );

  const handleValidationError = useCallback(() => {
    scrollToFirstError(form);
  }, [form]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      form.onSubmit(handleSubmit, handleValidationError)(event);
    },
    [form, handleSubmit, handleValidationError]
  );

  const errorMessage = useMemo(
    () =>
      createGrant.error
        ? classifyPortalGrantError(
            createGrant.error,
            CREATE_GRANT_ERROR_FALLBACKS
          )
        : null,
    [createGrant.error]
  );

  const submitLabel = useMemo(
    () => (mode === "primary" ? "Send Invitation" : "Add & Invite"),
    [mode]
  );

  const isLinkedToPhysician = useMemo(
    () => Boolean(form.values.linkedPhysicianId),
    [form.values.linkedPhysicianId]
  );

  const roleDescription = useMemo(() => {
    if (mode === "primary") {
      return "The primary contact's role is fixed.";
    }
    if (isLinkedToPhysician) {
      return "Linked to a physician — the role is fixed as Physician.";
    }
    return undefined;
  }, [mode, isLinkedToPhysician]);

  const helperText = useMemo(() => {
    if (mode !== "primary") {
      return null;
    }
    if (subjectType === "physician") {
      return "This is the physician's own portal access. Defaults are pre-filled from the physician record.";
    }
    return "Every practice or surgery center starts with a primary contact who will receive the portal invitation.";
  }, [mode, subjectType]);

  if (grantsLoading) {
    return (
      <Modal.Body style={{ flex: 1, overflow: "auto" }}>
        <Center py="md">
          <Loader size="sm" />
        </Center>
      </Modal.Body>
    );
  }

  return (
    // The form spans Modal.Body and the footer, so it has to carry the flex
    // column through — otherwise Modal.Content scrolls as a whole and takes
    // the footer buttons with it.
    <form
      onSubmit={handleFormSubmit}
      style={{ display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      <Modal.Body style={{ flex: 1, overflow: "auto" }}>
        <Stack gap="md">
          {revokedGrants.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Previously removed{" "}
                {revokedGrants.length > 1 ? "contacts" : "contact"}
              </Text>
              {revokedGrants.map((grant) => (
                <RevokedGrantCard
                  key={grant.id}
                  grant={grant}
                  onRestored={onClose}
                />
              ))}
            </Stack>
          )}

          {showAddForm ? (
            <>
              {helperText && (
                <Text size="sm" c="dimmed">
                  {helperText}
                </Text>
              )}
              {mode === "additional" &&
                supportsPhysicianLink &&
                physicianOptions.length > 0 && (
                  <Select
                    label="Link to physician (optional)"
                    placeholder="Choose a linked physician to auto-fill"
                    data={physicianOptions.map((p) => ({
                      value: p.value,
                      label: p.label,
                    }))}
                    value={form.values.linkedPhysicianId || null}
                    onChange={handlePhysicianSelect}
                    clearable
                    searchable
                  />
                )}
              <TextInput
                label="Email"
                placeholder="person@example.com"
                withAsterisk
                {...form.getInputProps("email")}
              />
              <Group grow>
                <TextInput
                  label="First name"
                  withAsterisk
                  {...form.getInputProps("firstName")}
                />
                <TextInput
                  label="Last name"
                  withAsterisk
                  {...form.getInputProps("lastName")}
                />
              </Group>
              <TextInput
                label="Role"
                placeholder="e.g. Office Manager, PA, Self"
                disabled={mode === "primary" || isLinkedToPhysician}
                description={roleDescription}
                {...form.getInputProps("role")}
              />
              <PortalNotificationPreferenceSelect
                {...form.getInputProps("notificationPreference")}
              />
              {errorMessage && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  variant="light"
                  title="Couldn't create grant"
                  role="alert"
                >
                  {errorMessage}
                </Alert>
              )}
            </>
          ) : (
            <Group justify="flex-end">
              <Button
                variant="subtle"
                size="sm"
                type="button"
                onClick={handleShowAddForm}
              >
                Invite someone new instead
              </Button>
            </Group>
          )}
        </Stack>
      </Modal.Body>

      <Divider />
      <Group justify="space-between" p="md">
        <Button variant="default" onClick={onClose} type="button">
          Cancel
        </Button>
        {showAddForm && (
          <Button type="submit" loading={createGrant.isPending}>
            {submitLabel}
          </Button>
        )}
      </Group>
    </form>
  );
}
